/**
 * Enhanced Supabase Client for Center Hub
 *
 * Provides authenticated Supabase client with job monitoring capabilities,
 * connection testing, and real-time subscription support.
 *
 * Created: November 3, 2025
 * Part of: Milestone 3 - Real-Time Status via Supabase (Center Hub)
 */

import { createClient } from '@supabase/supabase-js';

// ============================================================================
// ENVIRONMENT CONFIGURATION
// ============================================================================

/**
 * Get Supabase URL from environment
 * Falls back to hardcoded value for backward compatibility
 */
const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL ||
  'https://dlmcxxzohervnbixauhs.supabase.co';

/**
 * Get Supabase Anon Key from environment
 * Falls back to hardcoded value for backward compatibility
 */
const SUPABASE_ANON_KEY =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRsbWN4eHpvaGVydm5iaXhhdWhzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUxNzcwNzEsImV4cCI6MjA2MDc1MzA3MX0.UIQ7Wqo3R9aTYHYQHTFsHWGdhFFKLHjhEShlz2J2J-Q';

/**
 * Validate Supabase configuration
 * Logs warning if using default values
 */
const validateConfiguration = () => {
  const isConfigured =
    SUPABASE_URL !== 'YOUR_SUPABASE_URL' &&
    SUPABASE_ANON_KEY !== 'YOUR_SUPABASE_ANON_KEY';

  if (!isConfigured) {
    console.warn(
      '[Supabase] Using placeholder credentials. ' +
      'Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env.local'
    );
  }

  return isConfigured;
};

// Validate on module load
const isConfigured = validateConfiguration();

// ============================================================================
// SUPABASE CLIENT INSTANCE
// ============================================================================

/**
 * Main Supabase Client Instance
 *
 * This is the single source of truth for Supabase authentication and database access.
 * Import this client throughout the app to ensure consistent session state.
 *
 * Configuration:
 * - autoRefreshToken: Automatically refresh auth tokens before expiry
 * - persistSession: Save session to localStorage for persistence across page loads
 * - detectSessionInUrl: Automatically detect and handle auth callbacks (magic links)
 * - storageKey: Custom key for localStorage to avoid conflicts with other apps
 * - realtime: Enable real-time subscriptions for job status updates
 */
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    storage: window.localStorage,
    storageKey: 'community_services_auth',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10, // Limit real-time events to prevent overload
    },
  },
  global: {
    headers: {
      'X-Client-Info': 'center-hub-site@1.0.0',
    },
  },
});

// ============================================================================
// CONNECTION TESTING
// ============================================================================

/**
 * Test Supabase connection
 *
 * Performs a simple query to verify that the Supabase client can connect
 * to the database and execute queries successfully.
 *
 * @returns {Promise<{success: boolean, message: string, latency: number|null}>}
 *
 * @example
 * const result = await testConnection();
 * if (result.success) {
 *   console.log(`Connected in ${result.latency}ms`);
 * } else {
 *   console.error('Connection failed:', result.message);
 * }
 */
export const testConnection = async () => {
  const startTime = Date.now();

  try {
    // Test query: Get count of schema_configs (always exists)
    const { data, error } = await supabase
      .from('schema_configs')
      .select('id', { count: 'exact', head: true });

    const latency = Date.now() - startTime;

    if (error) {
      console.error('[Supabase] Connection test failed:', error.message);
      return {
        success: false,
        message: `Connection test failed: ${error.message}`,
        latency: null,
      };
    }

    console.log(`[Supabase] Connection successful (${latency}ms)`);
    return {
      success: true,
      message: 'Connected successfully',
      latency,
    };
  } catch (err) {
    const latency = Date.now() - startTime;
    console.error('[Supabase] Connection test error:', err);
    return {
      success: false,
      message: `Connection test error: ${err.message}`,
      latency,
    };
  }
};

/**
 * Test job_monitoring table access
 *
 * Verifies that the client can read from the job_monitoring table.
 * Useful for checking RLS policies and table existence.
 *
 * @returns {Promise<{success: boolean, message: string, recordCount: number|null}>}
 *
 * @example
 * const result = await testJobMonitoringAccess();
 * if (result.success) {
 *   console.log(`Can access job_monitoring table (${result.recordCount} records)`);
 * }
 */
export const testJobMonitoringAccess = async () => {
  try {
    const { count, error } = await supabase
      .from('job_monitoring')
      .select('*', { count: 'exact', head: true });

    if (error) {
      console.error('[Supabase] job_monitoring access test failed:', error.message);
      return {
        success: false,
        message: `Cannot access job_monitoring table: ${error.message}`,
        recordCount: null,
      };
    }

    console.log(`[Supabase] job_monitoring table accessible (${count} records)`);
    return {
      success: true,
      message: 'job_monitoring table accessible',
      recordCount: count,
    };
  } catch (err) {
    console.error('[Supabase] job_monitoring access test error:', err);
    return {
      success: false,
      message: `job_monitoring access test error: ${err.message}`,
      recordCount: null,
    };
  }
};

/**
 * Run comprehensive connection diagnostics
 *
 * Runs all connection tests and returns a detailed report.
 * Useful for troubleshooting and initial setup verification.
 *
 * @returns {Promise<{overall: boolean, tests: Object}>}
 *
 * @example
 * const diagnostics = await runDiagnostics();
 * console.table(diagnostics.tests);
 */
export const runDiagnostics = async () => {
  console.log('[Supabase] Running connection diagnostics...');

  const connectionTest = await testConnection();
  const jobMonitoringTest = await testJobMonitoringAccess();

  const results = {
    overall: connectionTest.success && jobMonitoringTest.success,
    tests: {
      configuration: {
        name: 'Configuration Check',
        status: isConfigured ? 'PASS' : 'FAIL',
        details: isConfigured
          ? 'Environment variables configured'
          : 'Using default credentials (configure REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY)',
      },
      connection: {
        name: 'Database Connection',
        status: connectionTest.success ? 'PASS' : 'FAIL',
        details: connectionTest.message,
        latency: connectionTest.latency,
      },
      jobMonitoring: {
        name: 'Job Monitoring Table Access',
        status: jobMonitoringTest.success ? 'PASS' : 'FAIL',
        details: jobMonitoringTest.message,
        recordCount: jobMonitoringTest.recordCount,
      },
    },
  };

  console.log('[Supabase] Diagnostics complete:', results);
  return results;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if Supabase is properly configured
 *
 * @returns {boolean} True if environment variables are set
 */
export const isSupabaseConfigured = () => {
  return isConfigured;
};

/**
 * Get current Supabase URL
 *
 * @returns {string} The Supabase project URL
 */
export const getSupabaseUrl = () => {
  return SUPABASE_URL;
};

/**
 * Check if user is authenticated
 *
 * @returns {Promise<boolean>} True if user has active session
 */
export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session !== null;
};

/**
 * Get current user
 *
 * @returns {Promise<Object|null>} User object or null if not authenticated
 */
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// ============================================================================
// REAL-TIME SUBSCRIPTION HELPERS
// ============================================================================

/**
 * Create a real-time subscription to job_monitoring table
 *
 * Subscribes to INSERT, UPDATE, and DELETE events for job status changes.
 *
 * @param {Function} callback - Callback function to handle real-time events
 * @param {Object} options - Subscription options
 * @param {string} options.center_id - Filter by center_id
 * @param {string} options.job_id - Filter by specific job_id
 * @param {string} options.status - Filter by status
 * @returns {Object} Subscription object with unsubscribe method
 *
 * @example
 * const subscription = subscribeToJobUpdates((payload) => {
 *   console.log('Job updated:', payload.new);
 * }, { center_id: 'abc-123' });
 *
 * // Later: unsubscribe
 * subscription.unsubscribe();
 */
export const subscribeToJobUpdates = (callback, options = {}) => {
  let channel = supabase
    .channel('job_monitoring_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_monitoring',
        filter: options.center_id ? `center_id=eq.${options.center_id}` : undefined,
      },
      (payload) => {
        console.log('[Supabase] Job update received:', payload);
        callback(payload);
      }
    )
    .subscribe();

  return {
    unsubscribe: () => {
      console.log('[Supabase] Unsubscribing from job updates');
      supabase.removeChannel(channel);
    },
  };
};

/**
 * Subscribe to specific job status changes
 *
 * More focused subscription for monitoring a single job.
 *
 * @param {string} job_id - The job ID to monitor
 * @param {Function} callback - Callback function to handle status changes
 * @returns {Object} Subscription object with unsubscribe method
 *
 * @example
 * const subscription = subscribeToJobStatus('IJ-1', (job) => {
 *   console.log(`Job ${job.job_id} is now ${job.status}`);
 * });
 */
export const subscribeToJobStatus = (job_id, callback) => {
  let channel = supabase
    .channel(`job_${job_id}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'job_monitoring',
        filter: `job_id=eq.${job_id}`,
      },
      (payload) => {
        console.log(`[Supabase] Job ${job_id} status changed:`, payload.new);
        callback(payload.new);
      }
    )
    .subscribe();

  return {
    unsubscribe: () => {
      console.log(`[Supabase] Unsubscribing from job ${job_id}`);
      supabase.removeChannel(channel);
    },
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

// Export client as default for convenience
export default supabase;

// Named exports for all utilities
export {
  supabase as supabaseClient,
  testConnection as testSupabaseConnection,
  testJobMonitoringAccess as testJobAccess,
  runDiagnostics as runSupabaseDiagnostics,
};

// ============================================================================
// AUTO-DIAGNOSTICS (Development Only)
// ============================================================================

/**
 * Run diagnostics on module load in development mode
 * This helps catch configuration issues early
 */
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_AUTO_TEST_SUPABASE === 'true') {
  console.log('[Supabase] Auto-diagnostics enabled (set REACT_APP_AUTO_TEST_SUPABASE=false to disable)');
  runDiagnostics().catch((err) => {
    console.error('[Supabase] Auto-diagnostics failed:', err);
  });
}
